import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { CreatePurchaseOrderPayload, InventoryItem, InventoryLocation, Vendor } from '../../../types/inventory';
import { useEscapeKey } from '../../../hooks/useEscapeKey';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  vendors: Vendor[];
  locations: InventoryLocation[];
  inventoryItems: InventoryItem[];
  onCreate: (payload: CreatePurchaseOrderPayload) => Promise<void>;
};

export const CreatePOModal: React.FC<Props> = ({ isOpen, onClose, vendors, locations, inventoryItems, onCreate }) => {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const [vendorId, setVendorId] = useState<string>(vendors[0]?.id || '');
  const [locationId, setLocationId] = useState<string>('');
  const [poNumber, setPoNumber] = useState<string>('');
  const [orderDate, setOrderDate] = useState<string>(today);

  const [itemSearch, setItemSearch] = useState('');
  const [selectedItemKey, setSelectedItemKey] = useState<string>('');
  const selectedItem = useMemo(() => {
    if (!selectedItemKey) return null;
    const [t, id] = selectedItemKey.split(':');
    return inventoryItems.find((x) => x.catalog_type === t && x.catalog_item_id === id) || null;
  }, [selectedItemKey, inventoryItems]);

  const [lineDescription, setLineDescription] = useState('');
  const [lineQty, setLineQty] = useState<number>(1);
  // PO line item unit_price represents buy price (unit cost)
  const [lineUnitPrice, setLineUnitPrice] = useState<string>('0.00');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filteredItems = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    const items = q
      ? inventoryItems.filter((x) => (x.name || '').toLowerCase().includes(q) || (x.sku || '').toLowerCase().includes(q))
      : inventoryItems;
    return [...items].sort((a, b) => a.name.localeCompare(b.name));
  }, [inventoryItems, itemSearch]);

  useEscapeKey(onClose, isOpen && !saving);

  if (!isOpen) return null;

  const defaultLocation = locations.find((l) => l.is_default) || locations[0];

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!vendorId) throw new Error('Select a vendor');
      if (!poNumber.trim()) throw new Error('PO number is required');
      if (!selectedItem) throw new Error('Select an item (stock-tracked)');
      if (!lineDescription.trim()) throw new Error('Description is required');
      if (!lineUnitPrice) throw new Error('Buy price (unit cost) is required');

      const payload: CreatePurchaseOrderPayload = {
        vendor_id: vendorId,
        location_id: locationId || defaultLocation?.id || null,
        po_number: poNumber.trim(),
        order_date: orderDate,
        line_items: [
          {
            description: lineDescription.trim(),
            line_number: 1,
            quantity_ordered: Number(lineQty),
            unit_price: lineUnitPrice,
            catalog_type: selectedItem.catalog_type,
            catalog_item_id: selectedItem.catalog_item_id,
          },
        ],
      };

      await onCreate(payload);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to create PO');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/20 dark:bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-xl border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Create Purchase Order</h3>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-gray-500 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-hover"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && <div className="text-sm text-red-600">{error}</div>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Vendor</label>
              <select
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="">Select vendor…</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Location</label>
              <select
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="">Default ({defaultLocation?.name || '—'})</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">PO Number</label>
              <input
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="PO-2025-0001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Order Date</label>
              <input
                type="date"
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-dark-text-primary mb-3">Line Item</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Find Item</label>
                <input
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Search products, medications, immunizations…"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Select Item (stock-tracked)</label>
                <select
                  value={selectedItemKey}
                  onChange={(e) => {
                    const key = e.target.value;
                    setSelectedItemKey(key);
                    const [t, id] = key.split(':');
                    const item = inventoryItems.find((x) => x.catalog_type === t && x.catalog_item_id === id);
                    if (item) {
                      // Default description to item name (user can edit)
                      setLineDescription((prev) => (prev.trim() ? prev : item.name));
                    }
                  }}
                  className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="">Select…</option>
                  {filteredItems.map((it) => (
                    <option key={`${it.catalog_type}:${it.catalog_item_id}`} value={`${it.catalog_type}:${it.catalog_item_id}`}>
                      {it.name} • {it.catalog_type.toUpperCase()}
                      {it.sku ? ` • SKU ${it.sku}` : ''}
                    </option>
                  ))}
                </select>
                {selectedItem?.unit_price ? (
                  <div className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                    Selling price (from Billing Catalog): ${selectedItem.unit_price}
                  </div>
                ) : selectedItem ? (
                  <div className="text-xs text-amber-700 mt-1">
                    No Billing Catalog price linked for this item yet.
                  </div>
                ) : null}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Description</label>
                <input
                  value={lineDescription}
                  onChange={(e) => setLineDescription(e.target.value)}
                  className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Auto-filled from selection; can be edited"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Quantity Ordered</label>
                <input
                  type="number"
                  min={1}
                  value={lineQty}
                  onChange={(e) => setLineQty(Number(e.target.value))}
                  className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Buy Price (unit cost)</label>
                <input
                  value={lineUnitPrice}
                  onChange={(e) => setLineUnitPrice(e.target.value)}
                  className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="89.50"
                />
              </div>
            </div>

            <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-3">
              Inventory items must be marked stock-tracked in the catalog to be receivable into lots.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-md text-sm font-medium text-gray-700 dark:text-dark-text-secondary bg-white dark:bg-dark-bg-tertiary hover:bg-gray-50 dark:hover:bg-dark-bg-hover"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
            disabled={saving}
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};


